<template>
  <!--
    TXP-806. Shown when writes to this device's storage are failing.

    Deliberately DIFFERENT from StorageWarningBanner.vue in three ways:

      1. It is NOT dismissible. StorageWarningBanner has a Close control because it warns
         about a risk. This banner reports that clinical documentation is not being saved
         right now. A dismissible warning about that is not a warning. It disappears only
         when a write actually succeeds.

      2. Its colour is `error`, not `warning`, because this is a failure, not a risk.

      3. Its z-index is above the other bottom-anchored snackbars in this application
         (Notify and StorageWarningBanner both use 9999) so it is never hidden behind them.
  -->
  <v-snackbar
    v-model="failed"
    :timeout="-1"
    color="error"
    transition="slide-y-reverse-transition"
    min-width="320"
    max-width="640"
    multi-line
    center
    bottom
    style="z-index: 10000"
  >
    <v-row align="center" align-content="center" justify="center" no-gutters>
      <v-icon class="ma-0 pl-2 pr-2" left>
        {{ icons.mdiCloudAlert }}
      </v-icon>

      <v-col>
        <div style="font-weight: 600; margin-bottom: 2px">
          Not saved to this device
        </div>
        <div style="font-size: 0.85rem; line-height: 1.25">
          {{ message }}
        </div>
      </v-col>

      <v-col cols="auto" class="pl-2">
        <v-btn
          small
          outlined
          color="white"
          @click="showGuidance"
        >
          What do I do?
        </v-btn>
      </v-col>
    </v-row>
  </v-snackbar>
</template>

<script>
import { mapState } from 'vuex'
import { mdiCloudAlert } from '@mdi/js'

export default {
  data() {
    return {
      icons: { mdiCloudAlert },
    }
  },
  computed: {
    ...mapState({
      saveHealth: state => state.saveHealth,
    }),

    // v-model on v-snackbar needs a writable value. The setter is deliberately a no-op
    // for the "hide it" direction: nothing in the interface may turn this banner off.
    // Only a successful write, via the clearSaveFailure mutation, may do that.
    failed: {
      get() {
        return !!(this.saveHealth && this.saveHealth.failed)
      },
      set() {
        // Intentionally empty. See the comment above.
      },
    },

    message() {
      return (this.saveHealth && this.saveHealth.message)
        ? this.saveHealth.message
        : 'Changes are not being saved on this device.'
    },

    isQuota() {
      return !!(this.saveHealth && this.saveHealth.isQuota)
    },
  },
  methods: {
    // Opens the application's existing confirmation dialog with plain-English
    // instructions. Reuses ConfirmationDialog, which App.vue already mounts as
    // this.$root.confirm, rather than introducing a new dialog component.
    showGuidance() {
      const spaceAdvice = this.isQuota
        ? '<li>This device has run out of storage space.</li>'
        : '<li>This device rejected the save. It may be out of space, or its local database may be damaged.</li>'

      const body = [
        '<p><strong>Your recent entries are only in this browser\'s memory. They are not on this device\'s disk yet.</strong></p>',
        '<ol style="text-align: left; padding-left: 1.2em">',
        '<li>Write down, or photograph with another device, anything you have entered since your last sync. Do this first.</li>',
        '<li>Tap Sync to send your work to the server. Once it is on the server it is safe.</li>',
        '<li>After the sync finishes, close and reopen the app.</li>',
        '<li>If the sync fails or you cannot sync, contact IT Support before entering anything else.</li>',
        '</ol>',
        '<p><strong>Why this happened:</strong></p>',
        '<ul style="text-align: left; padding-left: 1.2em">',
        spaceAdvice,
        '<li>This message will clear itself as soon as saving starts working again.</li>',
        '</ul>',
      ].join('')

      // `this.$root.confirm` is assigned by App.vue inside its updated() hook:
      //     if (this.$refs.confirm?.open) this.$root.confirm = this.$refs.confirm.open
      // Before App.vue's first update cycle it does not exist. Calling it then would throw a
      // TypeError at the exact moment the provider most needs guidance, so fall back to a
      // persistent snackbar instead.
      if (typeof this.$root.confirm !== 'function') {
        this.$store.dispatch('notify', {
          value: 'Write down anything you have entered since your last sync, then tap Sync. If the sync fails, contact IT Support before entering anything else.',
          color: 'error',
          timeout: -1,
        })

        return
      }

      this.$root.confirm({
        title: 'Your work is not being saved',
        titleIconColor: 'error',
        body,
        confirm: 'I understand',
        cancel: false,
        confirmColor: 'error',
        html: true,
        persistent: true,
      })
    },
  },
}
</script>
